/**
 * Operator branding manifest.
 *
 * Shipped as JSON at `{operatorManifestBaseUrl || assetsBaseUrl}/operators/{operatorId}/manifest.json` (`BootConfig`).
 *
 * Only fields declared here are trusted by the runtime; anything else is ignored.
 */
export interface OperatorManifest {
  /** Must match the operatorId resolved at boot — guard against mis-routed manifests. */
  operatorId: string;

  /**
   * The operator's release (non-empty; semver by convention). The branding phase only logs it: nothing
   * appends it to a URL, so a release that must bypass caches ships under new bundle URLs.
   */
  version: string;

  /**
   * The in-game branding bundle: a bundle name in the built assets manifest (the sample `pilot-brand`) or
   * the URL of a remote bundle descriptor `{ name?, assets: [{ alias, src, data? }], files? }` (`loadBundleByRef`).
   * The full branding phase loads it whole, with bounded retry; a failure boots the game unbranded.
   */
  bundleUrl: string;

  /**
   * Optional small bundle holding ONLY the boot loader's art, under the local `boot-assets` paths
   * `background-intro` and `logo`; named like `bundleUrl` (the sample `pilot-brand-loader`). Only the early
   * branding race loads it, and the race runs only when the operator id is known before any backend call
   * (`?operator=` or the config override). The loader is already on screen with the default art; the
   * operator's art replaces it when the manifest and this bundle arrive within the race's timeout
   * (`EARLY_BRANDING_TIMEOUT_MS`), else the default stays (decision 19). The boot waits on the race, so keep
   * this bundle small (~hundreds of KB). The intro splash and the in-game logo keep the local art.
   *
   * `null` / absent → the loader keeps the local default `boot-assets` art. In-game branding still
   * applies via `bundleUrl`, which the full branding phase loads after the core bundles.
   */
  loaderBundleUrl?: string | null;

  /** Optional SRI-style hash for a bundle integrity check. Reserved: validated as a string and kept; nothing checks it. */
  bundleChecksum?: string;

  /**
   * Declared override asset paths. A path not in this list is never served from the operator bundle,
   * even though the bundle is loaded whole; a listed path the bundle does not carry is served from the
   * shared bundle (`BrandedAssetResolver.resolveBundle`).
   *
   * Paths are the bundle-relative aliases the game looks up, exactly and without suffix (e.g.
   * "backgrounds/background", "symbols/atlases/symbols"). An allowlisted atlas replaces the whole
   * atlas, so it must carry every frame name the default one does.
   */
  assetOverrides: string[];

  /**
   * Gate: operators cannot ship a bundle targeting a newer client than the build. If present and newer
   * than the client's `gameVersion`, or the client's version is unknown, both branding phases skip the
   * operator and the game boots unbranded.
   */
  minCompatibleGameVersion?: string;

  /**
   * Reserved, not implemented: a path inside the operator bundle to a Spine skeleton meant to replace a
   * title's main character skeleton wholesale (the entry itself the approval, no `assetOverrides` listing;
   * the operator's skeleton keeping the default's animation and slot names). Validated as a non-empty
   * string and kept, trimmed; nothing reads it, so no skeleton is swapped.
   */
  characterSkeletonAssetPath?: string;

  /** Free-form metadata for the operator (display name, support URL, etc.). Opaque to the game. */
  meta?: Record<string, unknown>;
}

/**
 * Validation result for a parsed manifest payload.
 */
export type OperatorManifestValidation =
  | { ok: true; manifest: OperatorManifest }
  | { ok: false; reason: string };

/**
 * Validate a JSON payload against the expected manifest shape.
 * Does not trust extra fields — returns a clean, strictly-typed manifest on success.
 */
export function validateOperatorManifest(
  payload: unknown,
  expectedOperatorId: string,
): OperatorManifestValidation {
  if (!payload || typeof payload !== "object") {
    return { ok: false, reason: "manifest is not an object" };
  }

  const raw = payload as Record<string, unknown>;

  if (typeof raw.operatorId !== "string" || raw.operatorId.trim() === "") {
    return { ok: false, reason: "missing operatorId" };
  }

  if (raw.operatorId !== expectedOperatorId) {
    return {
      ok: false,
      reason: `operatorId mismatch: expected "${expectedOperatorId}", got "${raw.operatorId}"`,
    };
  }

  if (typeof raw.version !== "string" || raw.version.trim() === "") {
    return { ok: false, reason: "missing version" };
  }

  if (typeof raw.bundleUrl !== "string" || raw.bundleUrl.trim() === "") {
    return { ok: false, reason: "missing bundleUrl" };
  }

  if (!Array.isArray(raw.assetOverrides)) {
    return { ok: false, reason: "assetOverrides must be an array" };
  }

  const overrides: string[] = [];
  for (const entry of raw.assetOverrides) {
    if (typeof entry !== "string" || entry.trim() === "") {
      return { ok: false, reason: "assetOverrides must contain non-empty strings" };
    }
    overrides.push(entry);
  }

  if (
    raw.minCompatibleGameVersion !== undefined &&
    typeof raw.minCompatibleGameVersion !== "string"
  ) {
    return { ok: false, reason: "minCompatibleGameVersion must be a string" };
  }

  if (raw.bundleChecksum !== undefined && typeof raw.bundleChecksum !== "string") {
    return { ok: false, reason: "bundleChecksum must be a string" };
  }

  if (
    raw.loaderBundleUrl !== undefined &&
    raw.loaderBundleUrl !== null &&
    (typeof raw.loaderBundleUrl !== "string" || raw.loaderBundleUrl.trim() === "")
  ) {
    return { ok: false, reason: "loaderBundleUrl must be a non-empty string or null" };
  }

  let characterSkeletonAssetPath: string | undefined;
  if (raw.characterSkeletonAssetPath !== undefined) {
    if (
      typeof raw.characterSkeletonAssetPath !== "string" ||
      raw.characterSkeletonAssetPath.trim() === ""
    ) {
      return { ok: false, reason: "characterSkeletonAssetPath must be a non-empty string" };
    }
    characterSkeletonAssetPath = raw.characterSkeletonAssetPath.trim();
  }

  const manifest: OperatorManifest = {
    operatorId: raw.operatorId,
    version: raw.version,
    bundleUrl: raw.bundleUrl,
    assetOverrides: overrides,
  };

  if (typeof raw.loaderBundleUrl === "string" && raw.loaderBundleUrl.trim() !== "") {
    manifest.loaderBundleUrl = raw.loaderBundleUrl;
  } else {
    manifest.loaderBundleUrl = null;
  }

  if (raw.bundleChecksum) {
    manifest.bundleChecksum = raw.bundleChecksum as string;
  }
  if (raw.minCompatibleGameVersion) {
    manifest.minCompatibleGameVersion = raw.minCompatibleGameVersion as string;
  }
  if (characterSkeletonAssetPath) {
    manifest.characterSkeletonAssetPath = characterSkeletonAssetPath;
  }
  if (raw.meta && typeof raw.meta === "object") {
    manifest.meta = raw.meta as Record<string, unknown>;
  }

  return { ok: true, manifest };
}
